// NOTE: Please keep component names arranged alphabetically

import {ComponentsDoc} from './types';

export const componentsDoc: ComponentsDoc = {
  'mp-autoresizing-input': {
    events: {
      input: {},
      focus: {},
      blur: {},
      keypress: {},
      change: {},
    },
  },
  'mp-banner': {
    events: {
      action: {
        when: `call to action is clicked`,
      },
    },
  },
  'mp-bar-viz': {
    events: {
      barClick: {
        when: `when a bar is clicked`,
      },
    },
  },
  'mp-button': {
    events: {
      click: {
        when: `user clicks button`,
      },
    },
  },
  'mp-breadcrumbs': {
    events: {
      nodeClick: {
        when: `user clicks on a link node`,
        detail: `node: LinkNode, index: number`,
      },
      track: {
        when: `user clicks on a link node and track-name is set`,
        detail: `props: {...node: LinkNode, index: number, eventName: string}, eventPrefix : string, eventName : string`,
      },
    },
    props: {
      linkNodes: {
        tsType: `LinkNode[]`,
        description: `These represent each breadcrumb step and can link to other urls`,
      },
    },
  },
  'mp-button-bar': {
    events: {
      click: {
        when: `user clicks button`,
      },
    },
  },
  'mp-checkbox': {
    events: {
      click: {
        when: `user clicks checkbox`,
      },
    },
  },
  'mp-section': {
    events: {
      change: {
        when: `header is clicked to expand or collapse body`,
        detail: `state`,
      },
    },
  },
  'mp-confirm': {
    events: {
      submit: {
        when: `modal button gets clicked`,
        detail: `action`,
      },
      change: {
        when: `modal state changes`,
        detail: `state`,
      },
    },
  },
  'mp-create-entity-modal': {
    events: {
      cancel: {
        when: `user clicks cancel button`,
        detail: `name, description`,
      },
      save: {
        when: `user clicks save button`,
        detail: `value`,
      },
    },
    props: {
      existingEntityNames: {
        tsType: `string[]`,
        description: `Names of entities already created. Used for validation.`,
      },
    },
  },
  'mp-inline-cohorts-modal': {
    events: {
      stagedCohortUpdated: {
        when: `emitted from cohorts-query-builder`,
        detail: `stagedCohort`,
      },
      commitStagedCohort: {
        when: `user applies the inline cohort`,
      },
      change: {
        when: `mp-modal state change`,
        detail: `state`,
      },
      track: {
        detail: `eventName, props`,
      },
    },
    props: {
      context: {
        description: `MPContext`,
      },
      cohorts: {
        tsType: `string[]`,
        description: `List of fetched cohorts`,
      },
    },
  },
  'mp-calendar': {
    events: {
      selectDate: {
        when: `date (single or range) is selected`,
        detail: `date?, to?, from?`,
      },
    },
  },
  'mp-clickable-input': {
    events: {
      inputchange: {
        when: `value is changed`,
        detail: `value`,
      },
      inputfocus: {
        when: `input is focused`,
      },
      inputblur: {
        when: `input loses focus`,
      },
      save: {
        when: `user presses enter to save`,
        detail: `value`,
      },
    },
  },
  'mp-drop-menu': {
    events: {
      change: {
        when: `menu opens or closes`,
        detail: `state`,
      },
    },
    slots: {
      default: {
        description: `child element to be rendered inside the dropdown`,
      },
    },
  },
  'mp-drop-menu-pane': {
    events: {
      // TODO: camelCase consistency maybe ?
      'back-click': {
        when: `user clicks back`,
      },
    },
    slots: {
      default: {
        description: `child element to be rendered inside the dropdown`,
      },
    },
  },
  'mp-file-uploader': {
    events: {
      change: {
        when: `user selects a file. parent component is responsible for setting the 'completed-filename' attribute to file.name to indicate successful selection or upload`,
        detail: `file`,
      },
      delete: {
        when: `user clicks the delete icon next to the file name`,
      },
    },
  },
  'mp-input': {
    events: {
      input: {
        when: `input changes text`,
      },
      focus: {
        when: `input is focused`,
      },
      blur: {
        when: `input is blurred (unfocused)`,
      },
    },
    props: {
      value: {
        tsType: `string`,
        description: `value for underlying input elem`,
      },
    },
  },
  'mp-input-group': {
    events: {
      change: {
        when: `user changes text`,
      },
      submit: {
        when: `user clicks button or presses enter`,
      },
    },
  },
  'mp-items-menu': {
    events: {
      draw: {
        when: `List item is rendered`,
      },
      clickItem: {
        when: `List item is clicked`,
        detail: `item, modifierKeys`,
      },
      clickPill: {
        when: `Item pill is clicked`,
        detail: `item, modifierKeys`,
      },
      focusItem: {
        when: `List item is focused`,
        detail: `item`,
      },
      selectAll: {
        when: `Select All is clicked`,
        detail: `selectionState`,
      },
      selectAllMatching: {
        when: `Select All Matching is clicked`,
        detail: `matchingSections`,
      },
    },
    props: {
      keydownProxyEl: {
        tsType: `HTMLElement`,
        description: `focusable element on which keyboard up/down events are proxied from`,
      },
      sections: {
        tsType: `Section[]`,
        description: `pass sections via props for perf reasons`,
      },
      sectionsFromInputFn: {
        tsType: `(input: string) => Section[]`,
        description: `A function that generates an items-menu section based on search input`,
      },
    },
  },
  'mp-modal': {
    events: {
      change: {
        when: `modal opens or closes`,
        detail: `state`,
      },
    },
  },
  'mp-create-cohort-modal': {
    events: {
      save: {
        when: `user clicks save and input is valid`,
        detail: `value`,
      },
      cancel: {
        when: `user clicks cancel`,
        detail: `name, description`,
      },
    },
  },
  'mp-popover-card': {
    events: {
      action: {
        when: `an option is selected`,
        detail: `item`,
      },
      close: {
        when: `the x is clicked`,
      },
    },
  },
  'mp-actions-popover': {
    events: {
      action: {
        when: `an option is selected, cohort is created, or breakdown option is selected`,
        detail: `action, item`,
      },
      close: {
        when: `the component closes`,
      },
      openCohortModel: {
        when: `the cohort modal opens`,
        detail: `cohortDetail`,
      },
      cancelCohort: {
        when: `a users cancels when creating a cohort`,
        detail: `name, description, type`,
      },
    },
  },
  'mp-scroll-control': {
    props: {
      scrollEl: {
        tsType: `HTMLElement`,
        description: `Element that is scrolled. Scroll position (e.g. all the way left, all the way right or not scrollable at all) determines which buttons are available.`,
      },
      mousemoveEl: {
        tsType: `HTMLElement`,
        description: `Element that is monitored for mousemove and mouseleave events to determine which scroll buttons are visible. This should be a parent so mouseleave events work properly.`,
      },
    },
  },
  'mp-select': {
    events: {
      change: {
        when: `user selects a value`,
        detail: `value, item, modifierKeys`,
      },
      focusItem: {
        when: `user focuses on list item`,
        detail: `value, item`,
      },
      menuChange: {
        when: `drop menu opens or closes`,
        detail: `isOpen`,
      },
      backClick: {
        when: `when back button is clicked`,
      },
      searchFilterChange: {
        when: `when a search term has been typed`,
        detail: `searchFilter`,
      },
    },
    props: {
      sections: {
        tsType: `Section[]`,
        description: `pass sections via props for perf reasons`,
      },
      sectionsFromInputFn: {
        tsType: `(input: string) => Section[]`,
        description: `A function that generates an items-menu section based on search input`,
      },
      contextPanelTransform: {
        tsType: `(item: any) => TooltipData`,
        description: `A function that generates TooltipData from focused item for a mp-context-panel to show besides the dropdown`,
      },
    },
    slots: {
      'button-suffix': {
        description: `A lot for appending components within the dropdown's mp-button`,
      },
      trigger: {
        description: `A custom trigger element that when clicked on, opens the items-menu`,
      },
      body: {
        description: `A lot for the contents of mp-drop-mennu`,
      },
      footer: {
        description: `A lot for additional actions UI e.g Create new button or apply button`,
      },
    },
  },
  'mp-settings-li': {
    events: {
      switchChange: {
        when: `When switch is changed`,
      },
      'button-click': {
        // TODO: camelCase consistency
        when: `When button is clicked`,
      },
      editClicked: {
        when: `edit button is clicked`,
      },
      removeClick: {
        when: `remove button is clicked`,
      },
      fileAdded: {
        when: `File is added via file uploader`,
      },
      inputUpdate: {
        when: `input text changes`,
      },
      saveClick: {
        when: `Save button is clicked`,
      },
      cancelClick: {
        when: `Cancel button is clicked`,
      },
    },
  },
  'mp-sidenav': {
    events: {
      selectItem: {when: `Item is selected`, detail: `interface: SidenavItem`},
      resize: {
        when: `Sidenav changes size. This happens when user resizes/expands/collapses. Apply the provided width to the container holding the sidenav.`,
        detail: `{width: number}`,
      },
      footerAction: {
        when: `User clicks provided footer-action`,
        detail: `{value: string}`,
      },
      headerAction: {
        when: `User clicks provided header CTA`,
        detail: `{value: string}`,
      },
      overflowMenuSelect: {
        when: `User selects something from an item's overflow menu`,
        detail: `{item: SidenavSectionItem, menuItem: SectionItem}`,
      },
    },
  },
  'mp-switch': {
    events: {
      change: {
        when: `switch is turned on or off`,
        detail: `on`,
      },
    },
  },
  'mp-tag-input': {
    events: {
      change: {
        when: `tag is adding, added, removed`,
        detail: `action, tag, value`,
      },
    },
  },
  'mp-table': {
    events: {
      cellClick: {
        when: `cell is clicked`,
        detail: `row, field, context, target`,
      },
      rowClick: {
        when: `row is clicked`,
        detail: `row, rowIdx, target`,
      },
      rowDetailsVisibilityChangeEvent: {
        when: `one or more rows changed to be expanded or collapsed via individual toggle or collapse all rows`,
        detail: `changed`,
      },
      selectionChange: {
        when: `one or more rows changed selection via individual or select all checkboxes`,
        detail: `changed, isToggleAll, rows`,
      },
      sortChange: {
        when: `user clicks on up/down carets to change sort order`,
        detail: `sortField, sortOrder, rows, isHeaderClick`,
      },
      numResultsChange: {
        when: `the number of results (displayed rows) in the table has changed due to filtering or other updates`,
        detail: `numResults`,
      },
      track: {
        when: `track-name is specified and a tracking event is dispatched`,
        detail: `eventName, props`,
      },
    },
    props: {
      columns: {
        tsType: `TableColumn[]`,
        description: `List of columns to show and their config`,
      },
      rows: {
        tsType: `TableRow[]`,
        description: `List of items to display. Items can use custom mixins for cell rendering logic`,
      },
      rowDetailsRenderer: {
        tsType: `(row: TableRow) => VNode`,
        description: `Row details view to display additional information about a row. Use custom mixins for the rendering logic`,
      },
    },
  },
  'mp-tag': {
    events: {
      remove: {
        when: `tag is removed`,
        detail: `tagName`,
      },
      select: {
        when: `tag is selected`,
        detail: `tagName`,
      },
    },
  },
  'mp-revealable-text-editor': {
    events: {
      changedText: {
        when: `when save is clicked`,
        detail: `value`,
      },
      cancel: {
        when: `cancel is clicked`,
      },
    },
  },
  'mp-toast': {
    events: {
      action: {
        when: `cta action btn is clicked`,
      },
      close: {
        when: `toast is closed`,
      },
      undo: {
        when: `undo btn is clicked`,
      },
    },
  },
  'mp-toggle': {
    events: {
      change: {
        when: `DEPRECATED: toggle tab is changed. (use select)`,
        detail: `selected`,
      },
      select: {
        when: `user selects tab`,
        detail: `selected`,
      },
    },
  },
  'mp-user-list': {
    events: {},
  },
  'mp-query-report-header': {
    events: {
      actionclick: {
        when: `a custom defined control action is clicked`,
      },
      save: {
        when: `save is confirmed`,
      },
      reset: {
        when: `reset button is clicked`,
      },
      bookmarkselectoropen: {
        when: `bookmark selector is opened`,
      },
      bookmarkselectorclose: {
        when: `bookmark selector is closed`,
      },
      bookmarkselect: {
        when: `bookmark from bookmark drawer is selected`,
      },
      track: {
        when: `interaction is tracked`,
      },
    },
  },
  'svg-icon': {
    attrs: {
      icon: {
        type: `string`,
        description: `name of icon`,
      },
      empty: {
        type: `boolean`,
        description: `DEPRECATED: A falsey icon name will render an empty icon`,
      },
      color: {
        type: `string`,
        description: `svg fill of the icon. use svg-icon-style() stylus mixin`,
      },
    },
  },
};
